import {
  AppsRounded,
  ClearRounded,
  KeyboardArrowDownRounded,
  KeyboardArrowLeftRounded,
  KeyboardArrowRightRounded,
  LaunchRounded,
  PersonAddRounded
} from '@mui/icons-material';
import {
  Box,
  Button,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography
} from '@mui/material';
import { useState } from 'react';
import { Link as RRLink } from 'react-router-dom';
import { DRAWER_WIDTH } from '../../constants';
import { BACKSPACE_SYSTEM } from '../../constants/backspaceSystem';

interface MenuProps {
  openDrawer: boolean;
  onCloseDrawer: (event: React.KeyboardEvent | React.MouseEvent) => void;
}

export default function Menu({ openDrawer, onCloseDrawer }: MenuProps) {
  const alwaysExpandServices = true;
  const [open, setOpen] = useState<boolean>(alwaysExpandServices || true);

  function list(anchor: 'top' | 'left') {
    return (
      <Box
        sx={{
          width: anchor === 'top' ? 'auto' : `${DRAWER_WIDTH}px`,
          display: 'flex',
          flexDirection: 'column',
          height: '100%'
        }}
      >
        <Toolbar sx={{ display: { xs: 'none', lg: 'block' } }} />
        <Box
          sx={(theme) => ({
            display: { xs: 'flex', lg: 'none' },
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: theme.spacing(0, 1)
          })}
        >
          <Typography
            component={RRLink}
            to="/"
            sx={(theme) => ({
              ml: 1.5,
              pt: { sm: 1 },
              height: { sm: 64 },
              fontSize: { xs: '1.6rem', sm: '1.9rem' },
              letterSpacing: { xs: -1.5, sm: -2 },
              fontFamily: 'Comfortaa',
              fontWeight: 700,
              cursor: 'pointer',
              color: theme.palette.text.primary,
              textDecoration: 'none'
            })}
            onClick={onCloseDrawer}
          >
            backspace
          </Typography>
          <Tooltip title="Close">
            <IconButton
              size="large"
              color="inherit"
              aria-label="close drawer"
              onClick={onCloseDrawer}
            >
              {anchor === 'top' ? (
                <ClearRounded />
              ) : (
                <KeyboardArrowLeftRounded />
              )}
            </IconButton>
          </Tooltip>
        </Box>
        <Divider />
        <List sx={{ flex: 1 }}>
          {alwaysExpandServices ? (
            <ListItem sx={{ height: 40 }}>
              <AppsRounded color="action" sx={{ fontSize: 20, mr: 1 }} />
              <ListItemText primary="Services" />
            </ListItem>
          ) : (
            <ListItemButton
              sx={{ height: 32, mx: 1, my: 0.5, px: 1, borderRadius: '8px' }}
              onClick={() => setOpen(!open)}
            >
              <AppsRounded color="action" sx={{ fontSize: 20, mr: 1 }} />
              <ListItemText primary="Services" />
              {open ? (
                <KeyboardArrowDownRounded color="action" />
              ) : (
                <KeyboardArrowRightRounded color="action" />
              )}
            </ListItemButton>
          )}
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {BACKSPACE_SYSTEM.filter(({ isActive }) => isActive).map(
                ({ shortName, url }, index) => (
                  <ListItemButton
                    sx={{
                      height: 36,
                      mx: 1,
                      my: 0.5,
                      px: 1.5,
                      borderRadius: '8px'
                    }}
                    key={index}
                    onClick={() => {
                      window.open(url, '_blank');
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center">
                          <Typography
                            sx={(theme) => ({
                              fontSize: '1.25rem',
                              letterSpacing: -1,
                              fontFamily: 'Comfortaa',
                              fontWeight:
                                theme.palette.mode === 'light' ? 700 : 500
                            })}
                          >
                            space
                          </Typography>
                          <Typography
                            sx={(theme) => ({
                              fontSize: '0.9rem',
                              ml: 0.5,
                              pl: 0.5,
                              pr: 0.7,
                              py: 0.1,
                              background: theme.palette.gradient.main,
                              color: 'background.paper',
                              borderRadius: '6px',
                              letterSpacing: -0.75,
                              fontFamily: 'Comfortaa',
                              fontWeight:
                                theme.palette.mode === 'light' ? 600 : 700
                            })}
                          >
                            {shortName}
                          </Typography>
                        </Box>
                      }
                    />
                    <LaunchRounded color="action" sx={{ fontSize: 18 }} />
                  </ListItemButton>
                )
              )}
            </List>
          </Collapse>
        </List>
        <Box sx={{ p: 2 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PersonAddRounded />}
            fullWidth
            onClick={() => {
              window.open(
                `${process.env.REACT_APP_REQUEST_FOR_BACKSPACE_WEB_URL}`,
                '_blank'
              );
            }}
          >
            Request new user
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <div>
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          display: { xs: 'none', lg: 'block' },
          [`& .MuiDrawer-paper`]: {
            boxSizing: 'border-box'
          }
        }}
      >
        {list('left')}
      </Drawer>
      <Drawer
        anchor="left"
        open={openDrawer}
        onClose={onCloseDrawer}
        sx={{
          display: { xs: 'none', sm: 'block', lg: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            borderRadius: '0 16px 16px 0'
          }
        }}
      >
        {list('left')}
      </Drawer>
      <Drawer
        anchor="top"
        open={openDrawer}
        onClose={onCloseDrawer}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            borderRadius: '0 0 16px 16px'
          }
        }}
      >
        {list('top')}
      </Drawer>
    </div>
  );
}
